import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Building2, Users, Target, TrendingUp, AlertTriangle, Landmark, Sparkles, FileText, Download } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { safeDownloadBlob } from "../../lib/downloadUtils";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function Field({ label, children }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div>
      <h4 className="text-sm font-medium text-muted-foreground mb-1">{label}</h4>
      <p className="text-sm whitespace-pre-wrap leading-relaxed">{children}</p>
    </div>
  );
}

// Airtable guarda el seguimiento con **negrita** estilo markdown — sin tirar de una
// librería markdown completa por 1 sola marca, solo renderizamos ese patrón puntual.
function renderBoldMarkdown(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, idx) =>
    chunk.startsWith("**") && chunk.endsWith("**")
      ? <strong key={idx}>{chunk.slice(2, -2)}</strong>
      : <span key={idx}>{chunk}</span>
  );
}

const money = (v) => (v ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v) : null);

export function InitiativeTrackingTab({ initiative }) {
  const { token } = useAuth();
  const i = initiative || {};
  const [coverUrl, setCoverUrl] = useState(null);
  const hasQuarterly = i.tracking_quarterly && Object.values(i.tracking_quarterly).some((v) => v);
  const hasBudgetByYear = i.budget_by_year && Object.keys(i.budget_by_year).length > 0;
  const hasNoAirtableData = !i.airtable_id && !i.airtable_iniciativa_id;

  useEffect(() => {
    setCoverUrl(null);
    if (!i.cover_image || !i.id || !token) return;
    // P0 descargas seguras: pedimos una URL firmada de corta duración en vez
    // de apuntar directo a un endpoint público.
    fetch(`${API}/api/initiatives/${i.id}/cover-image-url`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCoverUrl(`${API}${d.url}`))
      .catch(() => {});
  }, [i.cover_image, i.id, token]);

  const handleDocDownload = async (doc, idx) => {
    try {
      await safeDownloadBlob(`${API}/api/initiatives/${i.id}/documents/${idx}/download`, doc.filename || "documento", token);
    } catch (e) {
      toast.error("Error al descargar el documento");
    }
  };

  return (
    <div className="space-y-4" data-testid="tracking-tab-content">
      {hasNoAirtableData && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            Esta iniciativa no tiene datos de seguimiento importados todavía. Los campos de seguimiento aparecerán aquí cuando estén disponibles.
          </CardContent>
        </Card>
      )}

      {coverUrl ? (
        <Card className="overflow-hidden">
          <img
            src={coverUrl}
            alt={i.name}
            className="w-full max-h-64 object-cover"
            data-testid="tracking-cover-image"
            onError={(e) => { e.currentTarget.closest(".overflow-hidden").style.display = "none"; }}
          />
        </Card>
      ) : (!i.cover_image && i.image_url) && (
        <Card className="overflow-hidden">
          <img
            src={i.image_url}
            alt={i.name}
            className="w-full max-h-64 object-cover"
            data-testid="tracking-image"
            onError={(e) => { e.currentTarget.closest(".overflow-hidden").style.display = "none"; }}
          />
        </Card>
      )}

      {(i.documents || []).length > 0 && (
        <Card data-testid="tracking-documents-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" />Documentos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {i.documents.map((doc, idx) => (
              <Button
                key={idx}
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={() => handleDocDownload(doc, idx)}
                data-testid={`tracking-document-download-${idx}`}
              >
                <Download className="w-3.5 h-3.5" /> {doc.filename}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}


      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-sky-500" />Equipo y partners</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Sponsor(s)">{[i.sponsor, ...(i.sponsors || [])].filter(Boolean).join(", ") || null}</Field>
            <Field label="Project Manager">{i.project_manager}</Field>
            <Field label="Partner / Consorcio">{i.partner}</Field>
            <Field label="Tipo de partner">{i.partner_type}</Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Landmark className="w-4 h-4 text-emerald-500" />Financiación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Organismo financiador">{i.organismo_financiador}</Field>
            <Field label="Convocatoria">{i.convocatoria}</Field>
            <Field label="Origen de fondos">{i.origen_fondos}</Field>
            {hasBudgetByYear && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Presupuesto por año</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(i.budget_by_year).map(([yr, v]) => (
                    <Badge key={yr} variant="outline">{yr}: {money(v)}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {(i.seguimiento_2026 || i.seguimiento_2025 || i.seguimiento_2024 || i.seguimiento_2023) && (
        <Card data-testid="seguimiento-anual-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-500" />Seguimiento anual</CardTitle>
          </CardHeader>
          <CardContent>
            {i.seguimiento_2026_headline && (
              <Badge variant="outline" className="text-xs mb-4" data-testid="seguimiento-2026-headline">{i.seguimiento_2026_headline}</Badge>
            )}
            {/* P0-5: timeline visual en lugar de bloques de texto sueltos — años más
                recientes arriba, línea vertical conectando cada hito. */}
            <div className="relative pl-6" data-testid="seguimiento-timeline">
              <div className="absolute left-[7px] top-1.5 bottom-1.5 w-px bg-border" />
              {[
                { year: "2026", text: i.seguimiento_2026 },
                { year: "2025", text: i.seguimiento_2025 },
                { year: "2024", text: i.seguimiento_2024 },
                { year: "2023", text: i.seguimiento_2023 },
              ]
                .filter((e) => e.text && e.text.trim().toUpperCase() !== "N/A")
                .map((entry, idx, arr) => (
                  <div key={entry.year} className={`relative ${idx < arr.length - 1 ? "pb-5" : ""}`} data-testid={`timeline-entry-${entry.year}`}>
                    <span className="absolute -left-6 top-0.5 w-3.5 h-3.5 rounded-full bg-blue-500 ring-4 ring-background" />
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-1">{entry.year}</p>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{renderBoldMarkdown(entry.text)}</p>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {hasQuarterly && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-amber-500" />Hitos trimestrales</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-4 gap-3">
            {["q1", "q2", "q3", "q4"].map((q) => (
              <div key={q} className="rounded-lg border p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{q.toUpperCase()}</p>
                <p className="text-sm whitespace-pre-wrap">{i.tracking_quarterly?.[q] || <span className="text-muted-foreground">Sin registrar</span>}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-violet-500" />Contexto y lógica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Situación de partida">{i.starting_situation || i.baseline_situation}</Field>
            <Field label="Lógica del piloto / plan de trabajo">{i.pilot_logic}</Field>
            <Field label="Avances técnicos">{i.technical_advances}</Field>
            <Field label="Beneficio esperado">{i.expected_benefit}</Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Riesgos y dificultades</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Riesgo (resumen)">{i.risk}</Field>
            <Field label="Dificultades">{i.difficulties}</Field>
            <Field label="Potencial de expansión">{i.expansion_potential}</Field>
          </CardContent>
        </Card>
      </div>

      {i.innovation_level && (
        <Card>
          <CardContent className="py-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-500" />
            <span className="text-sm"><strong>Nivel de innovación:</strong> {i.innovation_level}</span>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
